import type { ResearchConfigInput } from '@shared/config';
import { Info } from 'lucide-react';
import { Alert } from '../../components/ui/feedback';
import { SetupAnyWebsite } from './setup-any-website';
import { SetupCsv } from './setup-csv';

export interface StepSetupProps {
  config: ResearchConfigInput;
  onChange: (patch: Partial<ResearchConfigInput>) => void;
  errors: Record<string, string>;
}

/**
 * Per-source setup.
 *
 * Only the sources actually selected get a panel, so the step is empty (and skippable) for a
 * plain Google Maps run. Sources that need nothing configured say so rather than showing an
 * empty box.
 */
export function StepSetup({ config, onChange, errors }: StepSetupProps) {
  const sources = (config.sources ?? []);

  const panels = [
    sources.includes('linkedin') ? (
      <section key="linkedin">
        <h3 className="mb-2 text-[13px] font-semibold text-foreground">LinkedIn export</h3>
        <SetupCsv config={config} onChange={onChange} error={errors['linkedin']} />
      </section>
    ) : null,
    sources.includes('any_website') ? (
      <section key="any">
        <h3 className="mb-2 text-[13px] font-semibold text-foreground">Any Website</h3>
        <SetupAnyWebsite config={config} onChange={onChange} error={errors['any_website']} />
      </section>
    ) : null,
    sources.includes('yellow_pages') ? (
      <section key="directories">
        <h3 className="mb-2 text-[13px] font-semibold text-foreground">Directories</h3>
        <Alert tone="info">
          Directories are chosen automatically from the countries you search. Only directories that allow crawling are
          used — any that answer with a bot challenge are reported as blocked and skipped, never worked around.
        </Alert>
      </section>
    ) : null,
  ].filter(Boolean);

  if (panels.length === 0) {
    return (
      <div className="flex items-start gap-2 text-[13px] text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        The sources you chose need no extra setup. Continue to the search step.
      </div>
    );
  }

  return <div className="space-y-6">{panels}</div>;
}
